import config from "./config";
import BaseApiClient from "./baseApi";
const { serverUrl } = config;

class SponchifyApi extends BaseApiClient {
  getUserPlaylists() {
    return this.get(`/playlists`);
  }

  addPlaylist(title) {
    return this.post(`/playlists`, { title });
  }

  updatePlaylist(playlistId, data) {
    return this.patch(`/playlists/${playlistId}`, data);
  }

  deletePlaylist(playlistId) {
    return this.delete(`/playlists/${playlistId}`);
  }

  // Playlist song management
  addSongToPlaylist(playlistId, mediaId) {
    return this.post(`/playlists/${playlistId}/songs`, { mediaId });
  }

  removeSongFromPlaylist(playlistId, mediaId) {
    return this.delete(`/playlists/${playlistId}/songs/${mediaId}`);
  }

  // Playlist sharing methods
  getPlaylistShares(playlistId) {
    return this.get(`/playlists/${playlistId}/shares`);
  }

  addPlaylistShare(playlistId, sharedWithUserId, permission) {
    return this.post(`/playlists/${playlistId}/shares`, {
      sharedWithUserId,
      permission
    });
  }

  updatePlaylistShare(playlistId, shareId, permission) {
    return this.patch(`/playlists/${playlistId}/shares/${shareId}`, {
      permission
    });
  }

  removePlaylistShare(playlistId, shareId) {
    return this.delete(`/playlists/${playlistId}/shares/${shareId}`);
  }

  getMutualFriends(playlistId) {
    return this.get(`/playlists/${playlistId}/mutual-friends`);
  }

  getRecentUsers() {
    return this.get("/users/recent");
  }

  getUserProfile(username) {
    return this.get(`/users/profile/${username}`);
  }

  uploadProfilePic(avatar) {
    const formData = new FormData();
    formData.append("avatar", avatar);
    return this.post(`/users/me/avatar`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  follow(username) {
    return this.post(`/users/me/follows`, { username });
  }

  unfollow(username) {
    return this.delete(`/users/me/follows/${username}`);
  }

  getMyProfile() {
    return this.get("/users/me");
  }

  getInviteCode() {
    return this.post("/users/invites");
  }

  getMyInvites() {
    return this.get("/users/me/invites");
  }

  checkInviteCode(code) {
    return this.get(`/users/invites/${code}`);
  }

  signUp(data) {
    return this.post(`/users/signup`, data);
  }

  signIn(data) {
    return this.post(`/users/auth`, data);
  }

  requestPasswordReset(username) {
    return this.post(`/users/password-reset/request`, { username });
  }

  updateEmailForReset(username, email) {
    return this.post(`/users/password-reset/update-email`, { username, email });
  }

  validateResetToken(token) {
    return this.get(`/users/password-reset/validate/${token}`);
  }

  resetPassword(token, password, confirmPassword) {
    return this.post(`/users/password-reset/reset`, {
      token,
      password,
      confirmPassword,
    });
  }

  getSongList(playlist) {
    return this.get(`/songs${playlist ? "?playlist=" + playlist : ""}`);
  }

  getUserSongs(userId) {
    return this.get(`/songs?user=${userId}`);
  }

  pushSong(url, playlist, isPublic = true) {
    return this.post(`/songs`, { query: url, public: isPublic, playlist });
  }

  getUserJobs() {
    return this.get(`/songs/jobs`);
  }

  clearUserJobs() {
    return this.delete(`/songs/jobs`);
  }

  deleteSong(mediaId) {
    return this.delete(`/songs/${mediaId}`);
  }

  getSongAnalysis(mediaId) {
    return this.get(`/songs/${mediaId}/analysis`);
  }

  // DJ submission sessions
  createDjSession(maxLinks) {
    return this.post(`/dj-sessions`, { maxLinks });
  }

  // The DJ's aggregated crowd-submit feed across all active links.
  getDjFeed() {
    return this.get(`/dj-sessions`);
  }

  updateDjSession(token, maxLinks) {
    return this.post(`/dj-sessions/${token}/config`, { maxLinks });
  }

  closeDjSession(token) {
    return this.post(`/dj-sessions/${token}/close`);
  }

  getDjSessionStatus(token) {
    return this.get(`/dj-sessions/${token}`);
  }

  markDjSessionScanned(token) {
    return this.post(`/dj-sessions/${token}/scan`);
  }

  submitDjLinks(token, urls) {
    return this.post(`/dj-sessions/${token}/submissions`, { urls });
  }

  getDjSessionTracks(token) {
    return this.get(`/dj-sessions/${token}/tracks`);
  }

  // App
  getAppInfo() {
    return this.get(`/app`);
  }

  pushAppUpdate(updateFile, changeLog) {
    const formData = new FormData();
    formData.append("updateFile", updateFile);
    formData.append("changeLog", changeLog);
    return this.post(`/app`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  pushAdminNotification(to, title, body, data = {}) {
    return this.post("/app/notifications", { to, title, body, data });
  }

  // Push Notifications
  getVapidPublicKey() {
    return this.get("/push-subscriptions/vapid-public-key");
  }

  subscribeToPushNotifications(subscription) {
    return this.post("/push-subscriptions", { subscription });
  }

  unsubscribeFromPushNotifications(endpoint) {
    return this.delete("/push-subscriptions", { endpoint });
  }

  listPushSubscriptions() {
    return this.get("/push-subscriptions");
  }

  getPublicSettings() {
    return this.get("/app/public-settings");
  }

  getUsers() {
    return this.get("/users");
  }

  previewUserMerge(sourceUserId, targetUserId) {
    return this.post("/users/merge", {
      sourceUserId,
      targetUserId,
      preview: true
    });
  }

  mergeUsers(sourceUserId, targetUserId) {
    return this.post("/users/merge", {
      sourceUserId,
      targetUserId,
      preview: false
    });
  }

  getServerVersion() {
    return this.get("/app/version");
  }

  // Playback resume
  getPlaybackLast() {
    return this.get('/playback/last');
  }

  upsertPlaybackLast(payload) {
    return this.post('/playback/last', payload);
  }

  // Shuffle
  toggleShuffle() {
    return this.post('/playback/shuffle/toggle');
  }

  getShuffledSongs(params) {
    const query = new URLSearchParams();
    if (params.playlistId) query.set('playlistId', params.playlistId);
    if (params.virtualPlaylistType) query.set('virtualPlaylistType', params.virtualPlaylistType);
    if (params.currentSongMediaId) query.set('currentSongMediaId', params.currentSongMediaId);
    return this.get(`/playback/shuffle/songs?${query.toString()}`);
  }
}

const api = new SponchifyApi(serverUrl);
export default api;
